import React, { FC, memo, ReactElement, Suspense, useRef, useState } from 'react';
import styled from 'styled-components';
import Layout from 'antd/es/layout';
import Spin from 'antd/es/spin';

import AppBar, { TAppBarHandle } from './components/AppBar';
import SideBar, { TSideBarHandle } from './components/SideBar';
import SuspenseLoading from './components/SuspenseLoading';
import SideBarLoggedOut from './components/SideBar/SideBarLoggedOut';
import AppBarLoggedOut from './components/AppBar/AppBarLoggedOut';

type TSidebarRoutesProps = {
  children: ReactElement;
  userType: string | null;
  isLoggedIn: boolean;
};

const SidebarRoutes: FC<TSidebarRoutesProps> = ({ children, userType, isLoggedIn }) => {
  const appbarRef = useRef<TAppBarHandle>({
    logout: async () => {
      /* no-op: real logout is attached once AppBar mounts via useImperativeHandle */
    },
  });
  const sidebarRef = useRef<TSideBarHandle>(null);
  const [sideBarOpen, setSideBarOpen] = useState(false);

  const handleMenuClick = () => setSideBarOpen((open) => !open);

  if (!userType) {
    return (
      <SLoader>
        <Spin />
      </SLoader>
    );
  }

  return (
    <SLayout>
      {isLoggedIn ? (
        <>
          <SideBar
            onClose={handleMenuClick}
            sideBarOpen={sideBarOpen}
            onLogout={() => appbarRef.current.logout()}
            ref={sidebarRef}
          />
          <SInnerLayout>
            <AppBar handleMenuClick={handleMenuClick} ref={appbarRef} />
            <Suspense fallback={<SuspenseLoading />}>{children}</Suspense>
          </SInnerLayout>
        </>
      ) : (
        <>
          <SideBarLoggedOut
            onClose={handleMenuClick}
            sideBarOpen={sideBarOpen}
            onLogout={() => appbarRef.current.logout()}
            ref={sidebarRef}
          />
          <SInnerLayout>
            <AppBarLoggedOut
              handleMenuClick={() => {
                /* no-op: logged-out AppBar has no mobile drawer to toggle (matches legacy behavior) */
              }}
              ref={appbarRef}
            />
            <Suspense fallback={<SuspenseLoading />}>{children}</Suspense>
          </SInnerLayout>
        </>
      )}
    </SLayout>
  );
};

export default memo(SidebarRoutes);

const SLayout = styled(Layout)`
  min-height: 100vh;
  background: #eff3f5;
`;

const SInnerLayout = styled(Layout)`
  background: #eff3f5;
  flex-grow: 1;
  min-height: 100vh;
  position: relative;
  max-width: 100%;
`;

const SLoader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
`;
